/* iter55u — Extracted from UsersPanel.jsx.
 *
 * Archive-based delete button for a customer user. Requires typed
 * confirmation ("ARCHIVE") to guard against fat-finger clicks. Admin
 * role users are refused server-side; we also hide the button when
 * role === 'admin' to reduce user confusion.
 */
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  AlertDialog, AlertDialogTrigger, AlertDialogContent, AlertDialogHeader,
  AlertDialogTitle, AlertDialogDescription, AlertDialogFooter,
  AlertDialogAction, AlertDialogCancel,
} from '@/components/ui/alert-dialog';
import { Archive, Loader2 } from 'lucide-react';
import { adminApi } from '@/lib/api';
import { toast } from 'sonner';

export default function ArchiveUserButton({ user, onArchived }) {
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);

  if ((user.role || '').toLowerCase() === 'admin') {
    return (
      <Button
        size="sm"
        variant="ghost"
        disabled
        className="h-7 gap-1 text-[11.5px] text-muted-foreground opacity-40"
        title="Cannot archive an admin. Demote to 'ordinary' first."
        data-testid={`admin-user-archive-disabled-${user.user_id}`}
      >
        <Archive className="h-3.5 w-3.5" />
      </Button>
    );
  }

  const submit = async () => {
    if (confirm !== 'ARCHIVE') {
      toast.error('Type ARCHIVE to confirm.');
      return;
    }
    setBusy(true);
    try {
      const r = await adminApi.archiveUser(user.user_id, reason);
      toast.success(
        `Archived ${user.email || user.user_id}. ${r.project_count} project(s) moved to archive.`,
      );
      setOpen(false);
      setReason('');
      setConfirm('');
      onArchived?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Archive failed.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <AlertDialog open={open} onOpenChange={setOpen}>
      <AlertDialogTrigger asChild>
        <Button
          size="sm"
          variant="ghost"
          className="h-7 gap-1 text-[11.5px] text-destructive hover:text-destructive"
          data-testid={`admin-user-archive-${user.user_id}`}
          title="Move this user + their projects to the archive"
        >
          <Archive className="h-3.5 w-3.5" />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent data-testid={`admin-user-archive-dialog-${user.user_id}`}>
        <AlertDialogHeader>
          <AlertDialogTitle>
            Archive {user.email || user.name || user.user_id}?
          </AlertDialogTitle>
          <AlertDialogDescription>
            The user + all their projects are moved to the archive. They&apos;ll be
            logged out immediately and can no longer sign in. You can restore or
            permanently purge them later under <b>Admin → Archived Users</b>.
            Payment / royalty ledger rows are always preserved.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <div className="space-y-2">
          <div>
            <Label className="text-[11px]">Reason (optional, for audit log)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. Requested account deletion / GDPR / fraud"
              className="h-8 text-[12px]"
              data-testid={`admin-user-archive-reason-${user.user_id}`}
            />
          </div>
          <div>
            <Label className="text-[11px]">
              Type <span className="font-mono text-destructive">ARCHIVE</span> to confirm
            </Label>
            <Input
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="h-8 text-[12px] font-mono"
              data-testid={`admin-user-archive-confirm-${user.user_id}`}
            />
          </div>
        </div>
        <AlertDialogFooter>
          <AlertDialogCancel disabled={busy}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => { e.preventDefault(); submit(); }}
            disabled={busy || confirm !== 'ARCHIVE'}
            data-testid={`admin-user-archive-submit-${user.user_id}`}
            className="bg-destructive hover:bg-destructive/90"
          >
            {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Archive className="h-3.5 w-3.5" />}
            Archive
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}
